import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Card, Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import GradientButton from './GradientButton';

function EmploymentForm({ backlink, nextlink, title, subtitle }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    position: '',
    employer_name: '',
    city: '',
    start_date: '',
    end_date: '',
    currentlyWorking: false,
  });

  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (location.state) {
      setFormData({
        position: location.state.position || '',
        employer_name: location.state.employer_name || '',
        city: location.state.city || '',
        start_date: location.state.start_date || '',
        end_date: location.state.end_date || '',
        currentlyWorking: location.state.currentlyWorking || false,
        description: location.state.description || [],
        id: location.state.id || null,
      });
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === 'currentlyWorking') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
        end_date: checked ? '' : prev.end_date,
      }));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  useEffect(() => {
    const { position, employer_name, city, start_date, end_date, currentlyWorking } = formData;
    const isFormValid =
      (position || '').trim() !== '' &&
      (employer_name || '').trim() !== '' &&
      (city || '').trim() !== '' &&
      (start_date || '').trim() !== '' &&
      ((end_date || '').trim() !== '' || currentlyWorking);
    setIsValid(isFormValid);
  }, [formData]);

  const handleNext = () => {
    if (!isValid) return;

    navigate(nextlink, {
      state: {
        ...formData,
        description: formData.description || [],
        id: formData.id || null,
      },
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      paddingTop: '2rem',
      paddingBottom: '4rem',
      overflowX: 'clip',
      width: '100%'
    }}>
      <Container fluid className="px-2 px-md-3">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-4"
        >
          <h2 className="text-white fw-bold mb-2">
            {title}
          </h2>
          <p className="text-white-50">{subtitle}</p>
        </motion.div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="row justify-content-center"
        >
          <div className="col-12 col-lg-10 col-xl-8">
            <Card className="shadow-lg border-0" style={{ borderRadius: '20px', overflow: 'hidden' }}>
              <Card.Header style={{
                background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)',
                border: 'none',
                padding: '1.5rem'
              }}>
                <h5 className="text-white mb-0">
                  Informacje o zatrudnieniu
                </h5>
              </Card.Header>
              
              <Card.Body style={{ padding: '2rem' }}>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Stanowisko</Form.Label>
                    <Form.Control
                      name="position"
                      type="text"
                      placeholder="np. Tapicer"
                      value={formData.position}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                      className="py-2"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Pracodawca</Form.Label>
                    <Form.Control
                      name="employer_name"
                      type="text"
                      placeholder="np. Tomczyk i Wspólnicy"
                      value={formData.employer_name}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                      className="py-2"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">Miasto</Form.Label>
                    <Form.Control
                      name="city"
                      type="text"
                      placeholder="np. Warszawa"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                      className="py-2"
                    />
                  </Form.Group>

                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-medium">Data rozpoczęcia</Form.Label>
                        <Form.Control
                          name="start_date"
                          type="month"
                          value={formData.start_date}
                          onChange={handleChange}
                          required
                          style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                          className="py-2"
                        />
                      </Form.Group>
                    </div>

                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-medium">Data zakończenia</Form.Label>
                        <Form.Control
                          name="end_date"
                          type="month"
                          value={formData.end_date}
                          onChange={handleChange}
                          disabled={formData.currentlyWorking}
                          style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                          className="py-2"
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <div className="form-check mb-4" style={{ cursor: 'pointer' }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="currentlyWorking"
                      name="currentlyWorking"
                      checked={formData.currentlyWorking}
                      onChange={handleChange}
                      style={{ cursor: 'pointer' }}
                    />
                    <label
                      className="form-check-label fw-medium"
                      htmlFor="currentlyWorking"
                      style={{ cursor: 'pointer' }}
                    >
                      Obecnie tu pracuję
                    </label>
                  </div>
                </Form>
              </Card.Body>
            </Card>
            {/* Buttons */}
            <div className="row g-3 mt-2">
              <div className="d-flex justify-content-between">
                  <GradientButton
                    onClick={() => navigate(backlink)}
                    gradient="pink"
                  >
                    <FaArrowLeft className="me-2" />
                    Wstecz
                  </GradientButton>
                  <GradientButton
                    onClick={handleNext}
                    disabled={!isValid}
                    gradient="blue"
                  >
                    Dalej
                    <FaArrowRight className="ms-2" />
                  </GradientButton>
              </div>
            </div>
          </div>
        </motion.div>
      </Container>
    </div>
  );
}

export default EmploymentForm;